/* @moldura padrao */
/**
 * form.tsx (funcionarios) — A ficha da pessoa, pela GESTÃO.
 *
 * Em página, e não em modal: a ficha tem documentos, endereço, PIX, vínculo e
 * acesso — num modal isso briga por espaço com a tela que ficou atrás. É como o
 * irmão faz (`funcionarios/form.tsx` do `webapp-rm-ops`).
 *
 * O QUE ESTA TELA TEM A MAIS QUE "MEUS DADOS": o que é da EMPRESA — vínculo,
 * função, loja, admissão, situação — e o cadeado que congela a ficha depois de
 * conferida. O que é da pessoa (documentos, contato, PIX) ela mesma preenche em
 * Meus dados, e aqui aparece para conferência e correção.
 *
 * VÍNCULO ≠ FUNÇÃO (ver `045_funcoes_e_vinculo.sql`): vínculo diz se a pessoa é
 * empregada — é ele que decide quem entra em ponto, férias e folha; função diz o
 * que ela faz. A Luciana é sócia E vendedora, e por isso os dois campos existem
 * lado a lado em vez de um só.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomCampoData } from '@/components/CustomCamposData';
import {
  CustomCampoCPF,
  CustomCampoPIS,
  CustomCampoTelefone,
  CustomCampoTexto,
} from '@/components/CustomCamposMascara';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import { comprimirImagem } from '@/lib/imagem';
import type { Funcao, Funcionario, Situacao, Vinculo } from './tipos';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

interface UploadFoto {
  arquivo: string;
  url: string;
}

const TIPOS_PIX = [
  { valor: 'celular', rotulo: 'Celular' },
  { valor: 'cpf', rotulo: 'CPF' },
  { valor: 'email', rotulo: 'E-mail' },
  { valor: 'aleatoria', rotulo: 'Chave aleatória' },
];

/**
 * Espelha `CAMPOS_PESSOAIS` de `backend/app/servicos/servico_funcionarios.py`.
 * Quem não tem `funcionarios.pessoal.ver` recebe estes campos em branco e não
 * pode gravá-los — a tela desabilita e o PUT não os manda, para não apagar o
 * documento de ninguém sem querer.
 */
const CAMPOS_PESSOAIS = ['cpf', 'rg', 'pis', 'pix', 'pix_tipo', 'conta_salario'] as const;

const SITUACOES: { valor: Situacao; rotulo: string }[] = [
  { valor: 'ativo', rotulo: 'Ativo' },
  { valor: 'afastado', rotulo: 'Afastado' },
  { valor: 'desligado', rotulo: 'Desligado' },
];

const VINCULOS: { valor: Vinculo; rotulo: string; ajuda: string }[] = [
  { valor: 'funcionario', rotulo: 'Funcionário', ajuda: 'Entra em ponto, férias e folha.' },
  { valor: 'socio', rotulo: 'Sócio', ajuda: 'Fica fora de ponto, férias e folha.' },
];

export function FuncionarioFormPage() {
  const { id } = useParams<{ id: string }>();
  const [ficha, setFicha] = useState<Funcionario | null>(null);
  const [funcoes, setFuncoes] = useState<Funcao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { pode, usuario } = useAuth();

  // O eixo do ESCOPO: a ficha da própria pessoa vem sempre inteira, com
  // permissão ou sem. A dos OUTROS, só para quem tem `funcionarios.pessoal.ver`.
  const verPessoais =
    pode('funcionarios.pessoal.ver') || (!!ficha && ficha.usuario_id === usuario?.id);

  useEffect(() => {
    let vivo = true;
    Promise.all([
      api.get<Funcionario>(`/funcionarios/${id}`),
      api.get<Funcao[]>('/funcionarios/funcoes'),
    ])
      .then(([f, fs]) => {
        if (!vivo) return;
        setFicha(f);
        setFuncoes(fs);
      })
      .catch((err) =>
        setErro(
          err instanceof ApiError ? err.message : 'Não foi possível abrir a ficha.',
        ),
      )
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [id]);

  function campo<K extends keyof Funcionario>(chave: K, valor: Funcionario[K]) {
    setFicha((f) => (f ? { ...f, [chave]: valor } : f));
  }

  async function onArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const arq = e.target.files?.[0];
    if (!arq) return;
    setEnviandoFoto(true);
    try {
      const comprimida = await comprimirImagem(arq);
      const fd = new FormData();
      fd.append('arquivo', comprimida, arq.name);
      const resp = await api.upload<UploadFoto>('/funcionarios/me/foto', fd);
      campo('foto', resp.url);
      // O caminho relativo é o que o PUT grava; a URL é só para exibir aqui.
      await salvar({ foto: resp.arquivo });
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Falha ao enviar a foto.',
      );
    } finally {
      setEnviandoFoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function salvar(extra?: Record<string, unknown>) {
    if (!ficha) return;
    setSalvando(true);
    try {
      const corpo: Record<string, unknown> = {
        nome: ficha.nome,
        apelido: ficha.apelido,
        vinculo: ficha.vinculo,
        funcao_id: ficha.funcao_id,
        loja_id: ficha.loja_id,
        situacao: ficha.situacao,
        admissao: ficha.admissao,
        demissao: ficha.demissao,
        cpf: ficha.cpf,
        rg: ficha.rg,
        pis: ficha.pis,
        nascimento: ficha.nascimento,
        pix: ficha.pix,
        pix_tipo: ficha.pix_tipo,
        conta_salario: ficha.conta_salario,
        telefone: ficha.telefone,
        celular: ficha.celular,
        email: ficha.email,
        endereco: ficha.endereco,
        bairro: ficha.bairro,
        cidade: ficha.cidade,
        observacoes: ficha.observacoes,
        ...extra,
      };
      if (!verPessoais) {
        for (const campoPessoal of CAMPOS_PESSOAIS) delete corpo[campoPessoal];
      }
      const atualizada = await api.put<Funcionario>(`/funcionarios/${id}`, corpo);
      setFicha(atualizada);
      toast.success('Ficha salva.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function alternarBloqueio() {
    if (!ficha) return;
    try {
      const atualizada = await api.patch<Funcionario>(
        `/funcionarios/${id}/bloqueio`,
        { bloquear: !ficha.edicao_bloqueada },
      );
      setFicha(atualizada);
      toast.success(
        atualizada.edicao_bloqueada
          ? 'Ficha conferida e travada.'
          : 'Ficha liberada para a pessoa editar.',
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível mudar.');
    }
  }

  if (carregando) {
    return (
      <div className="container py-4 text-center text-secondary">
        <span className="spinner-border spinner-border-sm me-2" />
        Carregando…
      </div>
    );
  }

  if (erro || !ficha) {
    return (
      <div className="container py-3">
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {erro ?? 'Ficha não encontrada.'}
        </div>
        <Link to="/funcionarios" className="btn btn-link">
          Voltar para a lista
        </Link>
      </div>
    );
  }

  return (
    /* As ações ficam no topo: a ficha é longa, e obrigar a rolar até o fim para
       salvar (ou até o topo para desistir) é atrito à toa. */
    <CustomMolduraPagina
      titulo={ficha.apelido || ficha.nome}
      descricao="Dados da pessoa, vínculo e acesso ao sistema"
      caminho={[{ rotulo: 'Funcionários', para: '/funcionarios' }]}
      acoes={
        <>
          <button
            type="button"
            className="btn btn-sm btn-outline-theme"
            onClick={alternarBloqueio}
            title={
              ficha.edicao_bloqueada
                ? 'Liberar para a pessoa editar a própria ficha'
                : 'Travar a ficha depois de conferir'
            }
          >
            <i className={`bi ${ficha.edicao_bloqueada ? 'bi-unlock' : 'bi-lock'} me-1`}></i>
            {ficha.edicao_bloqueada ? 'Liberar edição' : 'Conferir e travar'}
          </button>
          <button
            className="btn btn-theme"
            onClick={() => salvar()}
            disabled={salvando}
          >
            {salvando && <span className="spinner-border spinner-border-sm me-1" />}
            Salvar
          </button>
        </>
      }
    >

      {ficha.edicao_bloqueada && (
        <div className="alert alert-info d-flex align-items-center" role="status">
          <i className="bi bi-lock me-2"></i>
          <div className="small">
            Ficha <strong>travada</strong>: a pessoa não consegue mais editar em
            "Meus dados". A gestão continua editando por aqui.
          </div>
        </div>
      )}

      <div className="card adminuiux-card">
        <div className="card-body">
          <div className="row gx-3 align-items-center mb-4">
            <div className="col-auto">
              {/* `img` direta, e não o `figure.coverimg` do tema: o coverimg
                  vira background por JS (`updateCoverImages`), que só roda na
                  montagem — numa navegação SPA a foto ficava com tamanho zero.
                  A lista já usa esta marcação, e ela não depende de script. */}
              {ficha.foto ? (
                <img
                  src={ficha.foto}
                  alt=""
                  className="rounded-circle object-fit-cover"
                  style={{ width: '70px', height: '70px' }}
                />
              ) : (
                <span className="avatar avatar-70 rounded-circle bg-theme-1 bg-opacity-10 text-theme-1 d-flex align-items-center justify-content-center">
                  <i className="bi bi-person h4 mb-0"></i>
                </span>
              )}
            </div>
            <div className="col-auto">
              <button
                className="btn btn-sm btn-link text-theme-1 px-0"
                onClick={() => fileRef.current?.click()}
                disabled={enviandoFoto}
              >
                {enviandoFoto ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <i className="bi bi-camera me-1"></i>
                )}
                Trocar foto
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={onArquivo}
              />
            </div>
            <div className="col text-end">
              {/* O ACESSO. Quem tem login aparece aqui; quem não tem, o aviso
                  diz que isso é normal — pessoa sem login existe (experiência,
                  extra de fim de semana). */}
              {ficha.usuario ? (
                <>
                  <span className="badge bg-theme-1-subtle text-theme-1">
                    <i className="bi bi-person-badge me-1"></i>
                    {ficha.usuario}
                  </span>
                  <div className="text-secondary small mt-1">
                    {ficha.perfil_nome ?? 'sem perfil'} ·{' '}
                    <Link to="/usuarios">gerenciar acesso</Link>
                  </div>
                </>
              ) : (
                <>
                  <span className="badge text-bg-light border">Sem login</span>
                  <div className="text-secondary small mt-1">
                    A pessoa existe sem acesso ao sistema.
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── O que é da EMPRESA ─────────────────────────────────────── */}
          <h6 className="text-secondary small text-uppercase mb-3">
            Vínculo e função
          </h6>
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <label className="form-label small text-secondary" htmlFor="fic-vinculo">
                Vínculo
              </label>
              <select
                id="fic-vinculo"
                className="form-select"
                value={ficha.vinculo}
                onChange={(e) => campo('vinculo', e.target.value as Vinculo)}
              >
                {VINCULOS.map((v) => (
                  <option key={v.valor} value={v.valor}>
                    {v.rotulo}
                  </option>
                ))}
              </select>
              <div className="form-text">
                {VINCULOS.find((v) => v.valor === ficha.vinculo)?.ajuda}
              </div>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small text-secondary" htmlFor="fic-funcao">
                Função
              </label>
              <select
                id="fic-funcao"
                className="form-select"
                value={ficha.funcao_id ?? ''}
                onChange={(e) =>
                  campo('funcao_id', e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">—</option>
                {funcoes.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
              <div className="form-text">O que a pessoa faz no dia a dia.</div>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small text-secondary" htmlFor="fic-situacao">
                Situação
              </label>
              <select
                id="fic-situacao"
                className="form-select"
                value={ficha.situacao}
                onChange={(e) => campo('situacao', e.target.value as Situacao)}
              >
                {SITUACOES.map((s) => (
                  <option key={s.valor} value={s.valor}>
                    {s.rotulo}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoData
                id="fic-admissao"
                label="Admissão"
                valor={ficha.admissao ?? ''}
                onChange={(v) => campo('admissao', v || null)}
              />
            </div>
            {ficha.situacao === 'desligado' && (
              <div className="col-12 col-md-4">
                <CustomCampoData
                  id="fic-demissao"
                  label="Desligamento"
                  valor={ficha.demissao ?? ''}
                  onChange={(v) => campo('demissao', v || null)}
                />
              </div>
            )}
            <div className="col-12 col-md-4">
              <CustomCampoTexto
                id="fic-loja"
                label="Loja"
                valor={ficha.loja_nome ?? ''}
                onChange={() => undefined}
                ajuda="Definida no cadastro de usuário."
              />
            </div>
          </div>

          {/* ── O que é da PESSOA ──────────────────────────────────────── */}
          <h6 className="text-secondary small text-uppercase mb-3">
            Dados pessoais
          </h6>
          {!verPessoais && (
            <div className="alert alert-light border small d-flex gap-2 py-2">
              <i className="bi bi-eye-slash text-secondary" />
              <span>
                CPF, RG, PIS, chave PIX e conta salário estão ocultos: eles
                dependem do acesso <strong>Ver documento, PIX e conta salário</strong>.
              </span>
            </div>
          )}
          <div className="row g-3">
            <div className="col-12 col-md-8">
              <CustomCampoTexto
                id="fic-nome"
                label="Nome completo"
                valor={ficha.nome}
                onChange={(v) => campo('nome', v)}
                ajuda="Como está no documento."
              />
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoTexto
                id="fic-apelido"
                label="Nome curto"
                valor={ficha.apelido ?? ''}
                onChange={(v) => campo('apelido', v)}
                ajuda="É como aparece nas listas."
              />
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoCPF
                id="fic-cpf"
                label="CPF"
                valor={ficha.cpf ?? ''}
                onChange={(v) => campo('cpf', v)}
                disabled={!verPessoais}
              />
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoData
                id="fic-nasc"
                label="Nascimento"
                valor={ficha.nascimento ?? ''}
                onChange={(v) => campo('nascimento', v || null)}
              />
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoTelefone
                id="fic-celular"
                label="Celular / WhatsApp"
                valor={ficha.celular ?? ''}
                onChange={(v) => campo('celular', v)}
                icone="bi-whatsapp text-success"
              />
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoTexto
                id="fic-rg"
                label="RG"
                valor={ficha.rg ?? ''}
                onChange={(v) => campo('rg', v)}
                disabled={!verPessoais}
              />
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoPIS
                id="fic-pis"
                label="PIS"
                valor={ficha.pis ?? ''}
                onChange={(v) => campo('pis', v)}
                disabled={!verPessoais}
              />
            </div>
            <div className="col-12 col-md-4">
              <CustomCampoTexto
                id="fic-email"
                label="E-mail"
                valor={ficha.email ?? ''}
                onChange={(v) => campo('email', v)}
                icone="bi-envelope"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small text-secondary" htmlFor="fic-pixtipo">
                Tipo da chave PIX
              </label>
              <select
                id="fic-pixtipo"
                className="form-select"
                value={ficha.pix_tipo ?? ''}
                onChange={(e) => campo('pix_tipo', e.target.value || null)}
                disabled={!verPessoais}
              >
                <option value="">—</option>
                {TIPOS_PIX.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-8">
              <CustomCampoTexto
                id="fic-pix"
                label="Chave PIX"
                valor={ficha.pix ?? ''}
                onChange={(v) => campo('pix', v)}
                icone="bi-key"
                ajuda="Para reembolso sem depender da conta salário."
                disabled={!verPessoais}
              />
            </div>
            <div className="col-12">
              <CustomCampoTexto
                id="fic-endereco"
                label="Endereço"
                valor={ficha.endereco ?? ''}
                onChange={(v) => campo('endereco', v)}
                icone="bi-geo-alt"
              />
            </div>
            <div className="col-12 col-md-6">
              <CustomCampoTexto
                id="fic-bairro"
                label="Bairro"
                valor={ficha.bairro ?? ''}
                onChange={(v) => campo('bairro', v)}
              />
            </div>
            <div className="col-12 col-md-6">
              <CustomCampoTexto
                id="fic-cidade"
                label="Cidade"
                valor={ficha.cidade ?? ''}
                onChange={(v) => campo('cidade', v)}
              />
            </div>
            <div className="col-12 col-md-6">
              <CustomCampoTexto
                id="fic-conta"
                label="Conta salário"
                valor={ficha.conta_salario ?? ''}
                onChange={(v) => campo('conta_salario', v)}
                icone="bi-bank"
                ajuda="Agência e conta, se já tiver."
                disabled={!verPessoais}
              />
            </div>
            <div className="col-12">
              <label className="form-label small text-secondary" htmlFor="fic-obs">
                Observações
              </label>
              <textarea
                id="fic-obs"
                className="form-control"
                rows={2}
                value={ficha.observacoes ?? ''}
                onChange={(e) => campo('observacoes', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="text-end mt-3">
        <button className="btn btn-theme" onClick={() => salvar()} disabled={salvando}>
          {salvando && <span className="spinner-border spinner-border-sm me-1" />}
          Salvar
        </button>
      </div>
    </CustomMolduraPagina>
  );
}
